import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { mergeThemeKeepingMode, useThemeStore } from '@/stores/themeStore'
import { defaultTheme, type ThemeConfig } from '@/types/theme'
import { systemService } from '@/services/systemService'
import { getFriendlyError } from '@/utils/getFriendlyError'

function normalizeLogoForApi(url: string | null | undefined) {
  if (url == null || url.trim() === '') return null
  return url.trim()
}

export function useConfiguracaoTheme() {
  const { theme, setTheme, resetTheme } = useThemeStore()
  const [draft, setDraft] = useState<ThemeConfig>({ ...defaultTheme, ...theme })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await systemService.getSettings()
        if (data.theme) {
          const chosenMode = useThemeStore.getState().theme.mode
          const mergedTheme = mergeThemeKeepingMode(
            { ...defaultTheme, ...data.theme },
            chosenMode,
          )
          setDraft((prev) => ({ ...prev, ...mergedTheme }))
          setTheme(mergedTheme)
        } else {
          setDraft((prev) => ({
            ...prev,
            brandPrimary: data.primaryColor ?? prev.brandPrimary,
            brandSecondary: data.secondaryColor ?? prev.brandSecondary,
            companyName: data.companyName ?? prev.companyName,
            logoUrl: data.companyLogo ?? prev.logoUrl,
            logoUrlDark: data.logoUrlDark ?? prev.logoUrlDark,
            logoUrlLight: data.logoUrlLight ?? prev.logoUrlLight,
          }))
        }
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao carregar configurações do sistema'))
      }
    }
    loadSettings()
  }, [setTheme])

  const updateDraft = useCallback(
    (partial: Partial<ThemeConfig>) => {
      setDraft((prev) => ({ ...prev, ...partial }))
      setTheme(partial)
    },
    [setTheme],
  )

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await systemService.updateSettings({
        companyName: draft.companyName,
        companyLogo: normalizeLogoForApi(draft.logoUrl),
        logoUrlDark: normalizeLogoForApi(draft.logoUrlDark),
        logoUrlLight: normalizeLogoForApi(draft.logoUrlLight),
        primaryColor: draft.brandPrimary,
        secondaryColor: draft.brandSecondary,
        theme: draft,
      })
      setTheme(draft)
      toast.success('Configurações salvas com sucesso!')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar no servidor'))
    } finally {
      setIsSaving(false)
    }
  }, [draft, setTheme])

  const handleReset = useCallback(() => {
    setDraft({ ...defaultTheme })
    resetTheme()
    toast.success('Tema restaurado para o padrão')
  }, [resetTheme])

  return {
    draft,
    updateDraft,
    setTheme,
    handleSave,
    handleReset,
    isSaving,
  }
}
